// File: src/components/profile/CreateSelectionModal.js

import React from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useProfile } from '../../context/ProfileContext';

const CreateSelectionModal = ({ isOpen, onClose, onSelectType }) => {
  const navigate = useNavigate();
  const { activeProfile, isDarkMode } = useProfile(); // 🌟 Re-introduced isDarkMode context

  const creationOptions = [
    {
      label: 'Song',
      description: 'Upload a single track or audio release',
      action: () => {
        navigate('/upload-music', { state: { artistProfile: activeProfile } });
        onClose();
      }
    },
    {
      label: 'Album',
      description: 'Compile an EP, album, or record',
      action: () => {
        if (onSelectType) onSelectType('album');
        onClose();
      }
    },
    {
      label: 'Announcement',
      description: 'New releases, tours, giveaways, promotions',
      action: () => {
        if (onSelectType) onSelectType('announcement');
        onClose();
      }
    }
  ];

  if (!isOpen) return null;

  return createPortal(
    <AnimatePresence mode="wait">
      {isOpen && (
        <div className={`fixed inset-0 z-[9999] flex items-center justify-center p-4 ${isDarkMode ? 'dark' : ''}`}>
          
          {/* 🌟 SOLID SEMI-TRANSPARENT BACKDROP (No GPU repaint flicker) */}
          <motion.div
            className="fixed inset-0 bg-black/75 transform-gpu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15, ease: "linear" }}
            onClick={onClose}
          />

          {/* 🌟 MAIN MODAL CARD WITH RESTORED BRAND DARK MODE STYLES */}
          <motion.div
            className={`relative z-10 w-full max-w-sm p-8 rounded-3xl shadow-2xl border transform-gpu transition-colors duration-200 ${
              isDarkMode 
                ? 'bg-brand-black border-brand-gray text-brand-white' 
                : 'bg-white border-slate-100 text-slate-900'
            }`}
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }}
            transition={{ duration: 0.15, ease: "easeOut" }}
            onClick={(e) => e.stopPropagation()} 
          >
            {/* Header Module */}
            <h2 className="text-xl font-bold mb-1">Create New Asset</h2>
            <p className={`text-xs mb-5 font-semibold ${isDarkMode ? 'text-brand-white/50' : 'text-gray-500'}`}>
              Select a creation type
            </p>

            {/* Selection Text List Grid */}
            <div className="space-y-2.5">
              {creationOptions.map((option) => (
                <button
                  key={option.label}
                  onClick={option.action}
                  className={`w-full p-4 text-left border rounded-2xl group transition-all focus:outline-none ${
                    isDarkMode 
                      ? 'bg-brand-gray/40 border-brand-gray hover:border-[#ff00cc]/40 hover:bg-brand-gray/60' 
                      : 'bg-slate-50 border-slate-200 hover:border-[#ff00cc]/40 hover:bg-slate-100/50'
                  }`}
                >
                  <h4 className={`font-bold text-sm group-hover:text-[#ff00cc] transition-colors leading-tight ${
                    isDarkMode ? 'text-brand-white' : 'text-slate-900'
                  }`}>
                    {option.label}
                  </h4>
                  <p className={`text-[11px] mt-1 leading-tight font-semibold ${
                    isDarkMode ? 'text-brand-white/40' : 'text-gray-500'
                  }`}>
                    {option.description}
                  </p>
                </button>
              ))}
            </div>

            {/* Action Footer Button Drawer */}
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={onClose}
                className={`px-4 py-2 rounded-md transition-colors text-sm font-semibold focus:outline-none ${
                  isDarkMode 
                    ? 'text-brand-white/50 hover:bg-brand-gray/20' 
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                Cancel
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default CreateSelectionModal;